import { getLocalTimeZone, today } from '@internationalized/date';
import {
  GridCalendar,
  capitalizeFirstLetter,
} from '@leagueplatform/engen-capability-shared-library/';
import {
  Radio,
  StackItem,
  StackLayout,
  UtilityText,
  queryHelpers,
  useMediaQuery,
} from '@leagueplatform/genesis-core';
import React, { useEffect, useMemo, useState } from 'react';
import { TobaccoUser } from '../data/tobaccoUser';
import { isTobaccoUser, notTobaccoUser } from '../helper/helpers';
import { useTobaccoStore } from '../store/tobacco-use.form-store';
import TobaccoUsageLabel from '../tobacco-use-labels.json';

function LastUsage(props: any) {
  const { tobaccoUser, dependentId } = props;
  const {
    tobaccoUseLastReportDate,
    hasPrepopulationError,
    hasRequiredError,
    isOffExchange,
  } = tobaccoUser;
  const currentDate = useMemo(() => today(getLocalTimeZone()), []);
  const minDate = currentDate.add({ days: 1 }).subtract({ months: 6 }); // 6 months before current Date

  const hasError = useMemo(
    () => hasPrepopulationError || hasRequiredError,
    [hasPrepopulationError, hasRequiredError],
  );

  const setLoginUserLastUseDate = useTobaccoStore(
    (state) => state.setLoginUserLastUseDate,
  );

  const setDependentLastUseDate = useTobaccoStore(
    (state) => state.setDependentLastUseDate,
  );

  const [calendarOpen, setCalendarOpen] = useState(false);

  const isBelow450 = useMediaQuery(queryHelpers.down(450));
  const isBelow520 = useMediaQuery(queryHelpers.down(520));

  const popoverContentAlignOffset = useMemo(() => {
    if (isBelow450) {
      return -47;
    }
    if (isBelow520) {
      return -112;
    }
    return 0;
  }, [isBelow450, isBelow520]);

  return (
    <GridCalendar
      popoverOpen={calendarOpen}
      setPopoverOpen={setCalendarOpen}
      popoverOnOpenChange={() => setCalendarOpen((previous) => !previous)}
      label={TobaccoUsageLabel.labels.tobacco_usage_date}
      inputStatus={hasError ? 'error' : undefined}
      statusMessage="Please enter a valid date."
      required
      formFieldCss={{
        '.GDS-input-wrapper': {
          minWidth: '200px',
          maxWidth: '250px',
          '@laptop': {
            maxWidth: '300px',
          },
          '@desktop': {
            maxWidth: '300px',
          },
        },
        '.gridCalendarTextInput': {
          width: '100%',
        },
      }}
      isTextBoxDisabled={!isOffExchange}
      selectedDate={tobaccoUseLastReportDate}
      setSelectedDate={(date: any) => {
        if (dependentId) {
          setDependentLastUseDate(date, dependentId);
        } else {
          setLoginUserLastUseDate(date);
        }
      }}
      popoverContentAlign={{
        '@initial': isBelow520 ? 'end' : 'center',
      }}
      popoverContentAlignOffset={popoverContentAlignOffset}
      popoverContentSide="bottom"
      popoverContentSideOffset={hasError ? 25 : 4}
      popoverContentAvoidCollisions={false}
      minLimit={minDate}
      maxLimit={currentDate}
      isCalendarButtonEnabled={isOffExchange}
      calendarPopoverCss={{
        '.calendar': {
          width: '500px',
          '@tablet': { width: '400px' },
          '@mobileLandscape': { width: '375px' },
          '@mobile': { width: isBelow450 ? '275px' : '375px' },
        },
      }}
    />
  );
}

function Fields(props: { tobaccoUser: TobaccoUser; dependentsKey?: string }) {
  const { tobaccoUser, dependentsKey } = props;

  const { tobaccoIndicator, coveredIndividualId, isOffExchange } = tobaccoUser;

  const setDependentTobaccoIndicator = useTobaccoStore(
    (state) => state.setDependentTobaccoIndicator,
  );
  const toggleIsDisabled = useTobaccoStore((state) => state.toggleIsDisabled);

  const setLoginUserTobaccoIndicator = useTobaccoStore(
    (state) => state.setLoginUserTobaccoIndicator,
  );

  useEffect(() => {
    toggleIsDisabled();
  });

  if (tobaccoUser.isOverTobaccoAgeLimit) {
    return (
      <StackLayout spacing="$oneAndHalf" horizontalAlignment="stretch">
        <StackItem>
          {!dependentsKey ? (
            <UtilityText emphasis="emphasized" css={{ paddingBottom: '12px' }}>
              {TobaccoUsageLabel.labels.reqular_user_you}
            </UtilityText>
          ) : (
            <UtilityText css={{ paddingBottom: '12px' }}>
              {capitalizeFirstLetter(tobaccoUser.firstName)}{' '}
              {capitalizeFirstLetter(tobaccoUser.lastName)}
            </UtilityText>
          )}

          {coveredIndividualId && (
            <StackLayout
              orientation="horizontal"
              css={{
                '&.GDS-horizontal-stack-layout > .GDS-radio-inline': {
                  margin: '$none $oneAndHalf $none $minusHalf',
                },
                '.GDS-c-doQXij:disabled:not(:checked)': {
                  backgroundColor: '#ffffff !important',
                },
              }}
            >
              <Radio
                id={`tobaccoUser_${coveredIndividualId}_Yes`}
                name={`tobaccoUser_${coveredIndividualId}`}
                label="Yes"
                layout="inline"
                value={tobaccoIndicator}
                onChange={() => {
                  if (dependentsKey)
                    setDependentTobaccoIndicator('T', dependentsKey);
                  else setLoginUserTobaccoIndicator('T');

                  // toggleIsDisabled();
                }}
                checked={
                  tobaccoIndicator ? isTobaccoUser(tobaccoIndicator) : false
                }
                disabled={!isOffExchange}
                aria-disabled={!isOffExchange}
              />
              <Radio
                id={`tobaccoUser_${coveredIndividualId}_No`}
                name={`tobaccoUser_${coveredIndividualId}`}
                label="No"
                layout="inline"
                value={tobaccoIndicator}
                onChange={() => {
                  if (dependentsKey)
                    setDependentTobaccoIndicator('N', dependentsKey);
                  else setLoginUserTobaccoIndicator('N');

                  // toggleIsDisabled();
                }}
                checked={
                  tobaccoIndicator ? notTobaccoUser(tobaccoIndicator) : false
                }
                disabled={!isOffExchange}
                aria-disabled={!isOffExchange}
              />
            </StackLayout>
          )}
        </StackItem>

        {isTobaccoUser(tobaccoIndicator) && (
          <StackItem>
            <LastUsage
              tobaccoIndicator={tobaccoIndicator}
              tobaccoUser={tobaccoUser}
              dependentId={dependentsKey}
            />
          </StackItem>
        )}
      </StackLayout>
    );
  }
  return null;
}

export default Fields;
